import type { Revenue, Tariff, FinanceGroupBy } from '../types/finance';
import type { User, UserRole } from '../types/user';
import { hayFinanzas } from './features';
import { formatearCOP } from './expenses';

// FE-32 — presentación, normalización y permisos de UI de finanzas, en un solo
// sitio (el equivalente de `utils/expenses.ts`).
//
// Lo que NO hay aquí: ninguna aritmética de fletes. La cuenta vive en el core
// (`/tariffs/{id}/quote`) y el frontend la pinta (ADR-030 §2).

/** Máximo del patrón `Quantity` del contrato: `^[0-9]{1,10}(\.[0-9]{1,4})?$`. */
const MAX_ENTEROS_CANTIDAD = 10;

/**
 * Convierte lo que una PERSONA escribe (`2.360`, `2360`, `1278,5`) en la
 * `Quantity` canónica del contrato (`"2360.0000"`), o `null`.
 *
 * Mismas reglas de separadores que `parseMontoCOP`: con los dos, el último es
 * el decimal; con uno solo, tres dígitos detrás = miles y uno a cuatro =
 * decimales. Cero y negativos devuelven `null`: una cantidad que no se conoce
 * no se manda.
 */
export function parseCantidad(texto: string): string | null {
  const limpio = texto.replace(/[\s]/g, '').trim();
  if (!limpio || !/^[\d.,]+$/.test(limpio)) return null;

  const puntos = (limpio.match(/\./g) ?? []).length;
  const comas = (limpio.match(/,/g) ?? []).length;

  let decimalEn = -1;
  if (puntos > 0 && comas > 0) {
    decimalEn = Math.max(limpio.lastIndexOf('.'), limpio.lastIndexOf(','));
  } else if (puntos === 1 || comas === 1) {
    const pos = puntos === 1 ? limpio.indexOf('.') : limpio.indexOf(',');
    const siguen = limpio.length - pos - 1;
    if (siguen >= 1 && siguen <= 4 && siguen !== 3) decimalEn = pos;
    // Tres dígitos detrás de un punto es ambiguo (2.360 kg ≈ dos mil
    // trescientos sesenta). Se lee como MILES, que es lo que un peso neto de
    // tiquete suele ser; una coma con tres dígitos se lee como decimal.
    if (siguen === 3 && comas === 1) decimalEn = pos;
  }

  const enterosCrudos = decimalEn === -1 ? limpio : limpio.slice(0, decimalEn);
  const enteros = enterosCrudos.replace(/[.,]/g, '');
  const decimales = decimalEn === -1 ? '' : limpio.slice(decimalEn + 1);

  if (/[.,]/.test(enterosCrudos) && !/^\d{1,3}([.,]\d{3})*$/.test(enterosCrudos)) return null;
  if (!/^\d+$/.test(enteros)) return null;
  if (decimales && !/^\d{1,4}$/.test(decimales)) return null;

  const sinCeros = enteros.replace(/^0+(?=\d)/, '');
  if (sinCeros.length > MAX_ENTEROS_CANTIDAD) return null;
  const canonico = `${sinCeros}.${decimales.padEnd(4, '0')}`;
  if (!/[1-9]/.test(canonico)) return null;
  return canonico;
}

const FORMATO_CANTIDAD = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 2 });

/** `"2360.0000"` → `"2.360"`; `"1175.7600"` → `"1.175,76"`. Con la unidad si se da. */
export function formatearCantidad(q: string | undefined, unit?: string): string {
  if (!q) return '—';
  const n = Number(q);
  if (Number.isNaN(n)) return q;
  const texto = FORMATO_CANTIDAD.format(n);
  return unit ? `${texto} ${unit}` : texto;
}

/** Resultado con signo: `"-692410.00"` → `"−$ 692.410"`. */
export function formatearResultado(amount: string): string {
  if (amount.startsWith('-')) return `−${formatearCOP(amount.slice(1))}`;
  return formatearCOP(amount);
}

/** `"8"` / `8` → `"8 %"`. */
export function formatearPorcentaje(pct: number): string {
  return `${FORMATO_CANTIDAD.format(pct)} %`;
}

/** La tarifa en una línea: `Fijo $ 18.761 + $ 149/kg · −8 % · mín. $ 361.742`. */
export function descripcionTarifa(t: Tariff): string {
  const partes: string[] = [];
  if (t.form === 'FLAT') {
    partes.push(`${formatearCOP(t.fixedAmount ?? '0')} por servicio`);
  } else {
    if (t.form === 'FIXED_PLUS_PER_UNIT' && t.fixedAmount) partes.push(`fijo ${formatearCOP(t.fixedAmount)}`);
    if (t.unitPrice) partes.push(`${formatearCOP(t.unitPrice)}/${t.unit ?? 'u'}`);
    if (t.discountPct > 0) partes.push(`−${formatearPorcentaje(t.discountPct)}`);
    if (t.minimumAmount) {
      partes.push(t.minimumThresholdQuantity
        ? `mín. ${formatearCOP(t.minimumAmount)} bajo ${formatearCantidad(t.minimumThresholdQuantity, t.unit)}`
        : `mín. ${formatearCOP(t.minimumAmount)}`);
    }
  }
  return partes.join(' · ');
}

/** `2026-07-01 → hoy`, `2026-01-01 → 2026-06-30`. */
export function vigenciaTexto(t: Tariff): string {
  return t.validTo ? `${t.validFrom} → ${t.validTo}` : `desde ${t.validFrom}`;
}

/** `true` si el importe cobrado NO es el que la tarifa propuso. Es lo que la pantalla marca. */
export function cobradoDistintoDelPropuesto(r: Revenue): boolean {
  if (!r.computedAmount) return false;
  return Number(r.computedAmount) !== Number(r.amount);
}

export function asociacionIngreso(r: Revenue): string {
  const partes: string[] = [];
  if (r.vehiclePlate) partes.push(r.vehiclePlate);
  else if (r.vehicleId) partes.push('vehículo');
  if (r.driverName) partes.push(r.driverName);
  else if (r.driverId) partes.push('conductor');
  if (r.tripId) partes.push('viaje asignado');
  return partes.length > 0 ? partes.join(' · ') : 'sin asociación';
}

export function sinSoporte(r: Revenue): boolean {
  return !r.attachment;
}

/** Hoy como `YYYY-MM-DD` local, lo que `validAt`, `from` y `to` esperan. */
export function hoyISODate(): string {
  return aISODate(new Date());
}

/** Un mes `YYYY-MM` como rango ISO (UTC) de instantes locales: 00:00 del 1 a 23:59:59.999 del último día. Sin mes válido, no filtra. */
export function rangoDeMes(mes: string): { from?: string; to?: string } {
  const [y, m] = mes.split('-').map(Number);
  if (!y || !m) return {};
  return rangoDeDias(aISODate(new Date(y, m - 1, 1)), aISODate(new Date(y, m, 0)));
}

/** Dos días locales `YYYY-MM-DD` (ambos inclusive) como rango ISO (UTC). Un extremo vacío queda abierto. */
export function rangoDeDias(desde: string, hasta: string): { from?: string; to?: string } {
  const r: { from?: string; to?: string } = {};
  if (desde) { const d = new Date(`${desde}T00:00:00`); if (!isNaN(d.getTime())) r.from = d.toISOString(); }
  if (hasta) { const h = new Date(`${hasta}T23:59:59.999`); if (!isNaN(h.getTime())) r.to = h.toISOString(); }
  return r;
}

export function aISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export const FINANCE_GROUPS: { id: FinanceGroupBy; label: string }[] = [
  { id: 'period', label: 'Por mes' },
  { id: 'vehicle', label: 'Por vehículo' },
  { id: 'subunit', label: 'Por sub-unidad' },
  { id: 'category', label: 'Por categoría de gasto' },
  { id: 'customer', label: 'Por cliente' },
];

// ---------------------------------------------------------------------------
// Permisos de UI — ADR-030 §3 (enmendado por el #225 el 2026-11-03), verificado
// contra backend/services/core/internal/console/scope.go (ActionViewRevenue,
// ActionRegisterRevenue, ActionCorrectRevenue, ActionManageTariffs,
// ActionViewFinance).
//
// ⚠️ Decide qué BOTÓN se ve, nunca si la operación es legítima: el backend
// responde 403/404 y manda.
//
// FE-35 (ADR-034): reciben el USUARIO y no solo el rol porque la primera
// pregunta ya no es «¿qué rol es?» sino «¿este despliegue tiene finanzas?».
// Con el módulo apagado todas responden `false`, sea cual sea el rol, y así
// ningún botón ni ninguna llamada del módulo llega a existir.
// ---------------------------------------------------------------------------

/** Lo que estas funciones necesitan saber de quien pregunta. */
type Usuario = Pick<User, 'role' | 'features'> | null | undefined;

/**
 * Ver lo que se COBRA al cliente: ingresos, su bitácora, sus soportes.
 *
 * El Operador NO (#225, issue #228). Antes "ver ingresos" no tenía casilla —lo
 * acotaba Visibility y el Operador veía los suyos con importe y todo—, y desde
 * el #225 `ActionViewRevenue` lo excluye: lo que se le cobra al cliente es de
 * los cargos superiores. Lo que el Operador sí hace, digitar el peso al iniciar
 * el viaje y subir la foto del manifiesto, cuelga de `ActionRunTrip`: el core
 * crea el ingreso en su nombre (ADR-032 §2) sin enseñarle el importe.
 */
export function puedeVerIngresos(user: Usuario): boolean {
  if (!hayFinanzas(user)) return false;
  const role = user?.role;
  return role === 'admin' || role === 'executive' || role === 'supervisor';
}

/** Quien ve los ingresos es quien los registra (#225): Administrador, Ejecutivo, Supervisor. */
export function puedeRegistrarIngreso(user: Usuario): boolean {
  return puedeVerIngresos(user);
}

export function puedeCorregirIngreso(user: Usuario): boolean {
  return puedeRegistrarIngreso(user);
}

/** Tarifas y clientes son la lista de precios de la empresa: Administrador y Ejecutivo. */
export function puedeGestionarTarifas(user: Usuario): boolean {
  if (!hayFinanzas(user)) return false;
  return user?.role === 'admin' || user?.role === 'executive';
}

/** El resultado (ingresos − gastos): Administrador y Ejecutivo el tenant, Supervisor su sub-unidad. El Operador no. */
export function puedeVerFinanzas(user: Usuario): boolean {
  return puedeVerIngresos(user);
}

const VENTANA_EDICION_OPERADOR_DIAS = 14;

/**
 * Misma ventana de #150 que los gastos: el Operador corrige 14 días.
 *
 * Desde el #225 el Operador no corrige ingresos, así que esta función devuelve
 * `true` para todos los roles que quedan. NO se borra: el #150 sigue abierto y
 * la ventana es su regla, no una consecuencia del reparto de roles.
 */
export function dentroDeVentanaEdicionIngreso(r: Revenue, role: UserRole | undefined): boolean {
  if (role !== 'operator') return true;
  const limite = Date.now() - VENTANA_EDICION_OPERADOR_DIAS * 24 * 60 * 60 * 1000;
  return Date.parse(r.createdAt) >= limite;
}

export function mensajeErrorFinanzas(
  err: unknown,
  accion: 'registrar' | 'corregir' | 'soporte' | 'tarifa' | 'cliente' | 'resumen' | 'cotizar',
): string {
  const status = (err as { status?: number })?.status;
  const detail = (err as { detail?: string })?.detail;

  if (status === 403) {
    if (accion === 'tarifa' || accion === 'cliente') return 'Solo un Administrador o un Ejecutivo pueden cambiar tarifas y clientes.';
    if (accion === 'resumen') return 'Tu rol no ve el resultado financiero.';
    return 'No tienes permiso para registrar ni corregir ingresos.';
  }
  if (status === 404) return 'El ingreso, el cliente, la tarifa, el vehículo, el conductor o el viaje no existe o está fuera de tu alcance.';
  if (status === 409) return detail ?? 'Ya existe un cliente con ese nombre.';
  if (status === 413) return 'El soporte supera el tope de 10 MB.';
  if (status === 501) return 'El almacén de soportes no está configurado en este entorno.';
  if (status === 400) {
    if (accion === 'soporte') return detail ?? 'El soporte tiene que ser una imagen (JPG, PNG, HEIC, WEBP) o un PDF, y pesar menos de 10 MB.';
    return detail ?? 'Revisa los datos: el servidor los rechazó.';
  }
  return detail ?? 'No se pudo completar la operación. Inténtalo de nuevo.';
}
